import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { useImpersonationStore } from '../store/impersonationStore';
import { clientApi, adminApi, Template, UsageSummary } from '../api/client';
import { format, subDays } from 'date-fns';

/**
 * Hook that returns templates for the current user context.
 * - API key users: uses clientApi (direct to FA with API key)
 * - Staff impersonating a tenant: uses adminApi.getTemplates filtered by tenant
 * - Staff with no tenant selected: uses adminApi.getTemplates (all templates)
 */
export function useClientTemplates(): UseQueryResult<Template[]> {
  const { authMode } = useAuthStore();
  const { selectedTenantId } = useImpersonationStore();
  const isStaff = authMode === 'aad';

  return useQuery({
    queryKey: ['client-templates', isStaff ? 'admin' : 'api', selectedTenantId],
    queryFn: () => {
      if (isStaff) {
        return adminApi.getTemplates(
          selectedTenantId ? { tenantId: selectedTenantId } : undefined
        );
      }
      return clientApi.getTemplates();
    },
  });
}

/**
 * Hook that returns usage data for the current user context.
 * - API key users: uses clientApi (direct to FA with API key)
 * - Staff impersonating a tenant: uses adminApi.getUsageAnalytics filtered by tenant
 * - Staff with no tenant selected: uses adminApi.getUsageAnalytics (all tenants)
 */
export function useClientUsage(startDate?: string, endDate?: string): UseQueryResult<UsageSummary> {
  const { authMode } = useAuthStore();
  const { selectedTenantId } = useImpersonationStore();
  const isStaff = authMode === 'aad';

  const defaultStart = format(subDays(new Date(), 30), 'yyyy-MM-dd');
  const defaultEnd = format(new Date(), 'yyyy-MM-dd');
  const start = startDate || defaultStart;
  const end = endDate || defaultEnd;

  return useQuery({
    queryKey: ['client-usage', isStaff ? 'admin' : 'api', selectedTenantId, start, end],
    queryFn: async (): Promise<UsageSummary> => {
      if (isStaff) {
        // Admin usage analytics returns a different shape — normalize it
        const data = await adminApi.getUsageAnalytics(start, end, selectedTenantId || undefined) as Record<string, unknown>;
        // Adapt admin response to UsageSummary shape for client pages
        return {
          period: { start, end },
          totalLabels: (data.totalLabels as number) || 0,
          successfulLabels: (data.successfulLabels as number) || (data.totalLabels as number) || 0,
          failedLabels: (data.failedLabels as number) || 0,
          uniqueTemplates: (data.uniqueTemplates as number) || 0,
          avgLatencyMs: (data.avgLatencyMs as number) || 0,
          byTemplate: (data.byTemplate as UsageSummary['byTemplate']) || [],
          byDay: (data.byDay as UsageSummary['byDay']) || (data.dailyUsage as UsageSummary['byDay']) || [],
        };
      }
      return clientApi.getUsage(start, end);
    },
  });
}
